import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
  Autocomplete, // Add this
  InputAdornment, // Add this
  IconButton, // Add this
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  Settings as SettingsIcon,
  GitHub as GitHubIcon,
  Assessment as JiraIcon,
  Cloud as SonarQubeIcon,
  Code as BitbucketIcon,
  People as PodIcon,
  CloudQueue as AzureIcon,
  Visibility, // Add this
  VisibilityOff, // Add this
} from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useAuthToken } from "../TokenContext";

// Styled components
const PremiumPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(13, 15, 22, 0.7)',
  backdropFilter: 'blur(20px)',
  padding: theme.spacing(5),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: '28px',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  width: '100%',
  maxWidth: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0, height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(61, 127, 255, 0.3), transparent)',
  }
}));

const CenteredContainer = styled(Container)(({ theme, collapsed }) => ({
  maxWidth: '1000px !important',
  margin: '0 auto',
  padding: theme.spacing(4),
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  fontWeight: 800,
  fontSize: '2.5rem',
  letterSpacing: '-1px',
  marginBottom: theme.spacing(1),
  fontFamily: '"Syne", sans-serif',
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.5)',
  marginBottom: theme.spacing(5),
  fontSize: '1.1rem',
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '20px',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  border: '1px solid rgba(255, 255, 255, 0.03)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.04)',
    borderColor: 'rgba(61, 127, 255, 0.2)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

const IconWrapper = styled(Box)(({ color }) => ({
  width: '42px',
  height: '42px',
  borderRadius: '12px',
  background: alpha(color || '#3d7fff', 0.1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: color || '#3d7fff',
  border: `1px solid ${alpha(color || '#3d7fff', 0.2)}`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '14px',
  padding: '16px 48px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '16px',
  background: 'linear-gradient(135deg, #3d7fff 0%, #2e61ff 100%)',
  boxShadow: '0 10px 25px rgba(61, 127, 255, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #4d8aff 0%, #3d7fff 100%)',
    boxShadow: '0 15px 35px rgba(61, 127, 255, 0.5)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.07)',
      transition: 'all 0.2s ease',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(61, 127, 255, 0.4)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3d7fff',
      borderWidth: '2px',
    },
    '& input': {
      color: '#ffffff',
      padding: '16px',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.4)',
    '&.Mui-focused': {
      color: '#3d7fff',
    },
  },
}));

const ModernSelect = styled(FormControl)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.07)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(61, 127, 255, 0.4)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3d7fff',
    },
    '& .MuiSelect-select': {
      color: '#ffffff',
      padding: '16px',
    },
    '& .MuiSvgIcon-root': {
      color: 'rgba(255, 255, 255, 0.4)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.4)',
    '&.Mui-focused': {
      color: '#3d7fff',
    },
  },
}));

const LoadingOverlay = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: '#fff',
  backgroundColor: 'rgba(8, 9, 13, 0.8)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

function EditTalentSettingsForm({ collapsed }) {
  const { user } = useSelector((state) => state.auth);
  const token = useAuthToken();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    id: user.data[0].c_id,
    podID: '',
    github_repo: '',
    github_username: '',
    jira_username: '',
    sonarqube_project_key: '',
    bitbucket_repo_slug: '',
    bitbucket_username: '',
    ado_assignee_name: ""
  });

  const [podList, setPodList] = useState([]);
  const [podSelected, setPodSelected] = useState(false);

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const showNotification = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  useEffect(() => {
    const fetchPodList = async () => {
      try {
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        };
        const response = await axios.post(
          process.env.REACT_APP_TALENT_PODLISTTALENT,
          { candidateID: user.data[0].c_id },
          { headers }
        );
        setPodList(response.data.data);
      } catch (error) {
        console.error('Error fetching pod list:', error);
        showNotification('Failed to fetch pod list', 'error');
      }
    };

    fetchPodList();
  }, [token, user.data]);

  useEffect(() => {
    if (formData.podID) {
      const fetchData = async () => {
        setIsLoading(true); // Start loading
        try {
          const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          };
          const response = await axios.post(
            process.env.REACT_APP_TALENT_GETALENTSETTING,
            {
              candidateID: user.data[0].c_id,
              podID: formData.podID
            },
            { headers }
          );
          const data = response.data.body.data[0];
          setFormData(prev => ({
            ...prev,
            github_repo: data?.github_repo || '',
            github_username: data?.github_username || '',
            jira_username: data?.jira_username || '',
            sonarqube_project_key: data?.sonarqube_project_key || '',
            bitbucket_repo_slug: data?.bitbucket_repo_slug || '',
            bitbucket_username: data?.bitbucket_username || '',
            ado_assignee_name: data?.ado_assignee_name || "",
          }));
          setPodSelected(true);
        } catch (error) {
          console.error('Error fetching data:', error);
          showNotification('Failed to fetch settings', 'error');
        }
        finally {
          setIsLoading(false); // Stop loading regardless of outcome
        }
      };

      fetchData();
    }
  }, [formData.podID, token, user.data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      await axios.put(
        process.env.REACT_APP_TALENT_EDITTALENTSETTINGS,
        formData,
        { headers }
      );
      showNotification('Settings updated successfully!');
    } catch (error) {
      console.error('Error updating settings:', error);
      showNotification('Failed to update settings', 'error');
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #08090d 0%, #111420 100%)',
      pt: 12, pb: 8
    }}>
      <CenteredContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <MainTitle variant="h1">
            Talent Settings
          </MainTitle>
          <SubTitle>
            Configure your development ecosystem and autonomous agent integrations.
          </SubTitle>
        </Box>

        <PremiumPaper elevation={0}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              {/* Pod Selection Section */}
              <Grid item xs={12}>
                <SectionContainer>
                  <SectionHeader>
                    <IconWrapper color="#3d7fff">
                      <PodIcon />
                    </IconWrapper>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>Pod Context</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Select the delivery pod to configure</Typography>
                    </Box>
                  </SectionHeader>
                  <ModernSelect>
                    <InputLabel>Select Active Pod</InputLabel>
                    <Select
                      name="podID"
                      value={formData.podID}
                      onChange={handleChange}
                      label="Select Active Pod"
                      required
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: '#111420',
                            borderRadius: '16px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: '#ffffff',
                            '& .MuiMenuItem-root:hover': { background: 'rgba(61, 127, 255, 0.1)' },
                            '& .Mui-selected': { background: 'rgba(61, 127, 255, 0.2) !important' }
                          }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>Select a Pod</em>
                      </MenuItem>
                      {podList.map((pod) => (
                        <MenuItem key={pod.podID} value={pod.podID}>
                          {pod.podName}
                        </MenuItem>
                      ))}
                    </Select>
                  </ModernSelect>
                </SectionContainer>
              </Grid>

              {isLoading && (
                <LoadingOverlay open={true}>
                  <CircularProgress size={48} sx={{ color: '#3d7fff' }} />
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                    Synchronizing Pod Intelligence...
                  </Typography>
                </LoadingOverlay>
              )}

              {/* Only render form sections if pod is selected and not loading */}
              {podSelected && !isLoading && (
                <>
                  {/* GitHub Section */}
                  <Grid item xs={12} md={6}>
                    <SectionContainer sx={{ height: '100%' }}>
                      <SectionHeader>
                        <IconWrapper color="#4ade80">
                          <GitHubIcon />
                        </IconWrapper>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>Version Control</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>GitHub Integration</Typography>
                        </Box>
                      </SectionHeader>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <ModernTextField
                            fullWidth
                            label="Target Repository"
                            name="github_repo"
                            value={formData.github_repo}
                            onChange={handleChange}
                            placeholder="org/repo-name"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <ModernTextField
                            fullWidth
                            label="GitHub Handle"
                            name="github_username"
                            value={formData.github_username}
                            onChange={handleChange}
                          />
                        </Grid>
                      </Grid>
                    </SectionContainer>
                  </Grid>

                  {/* Jira Section */}
                  <Grid item xs={12} md={6}>
                    <SectionContainer sx={{ height: '100%' }}>
                      <SectionHeader>
                        <IconWrapper color="#3b82f6">
                          <JiraIcon />
                        </IconWrapper>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>Task Management</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Jira Configuration</Typography>
                        </Box>
                      </SectionHeader>
                      <ModernTextField
                        fullWidth
                        label="Jira Assignee Email/ID"
                        name="jira_username"
                        value={formData.jira_username}
                        onChange={handleChange}
                      />
                    </SectionContainer>
                  </Grid>

                  {/* SonarQube & Azure Section */}
                  <Grid item xs={12} md={6}>
                    <SectionContainer sx={{ height: '100%' }}>
                      <SectionHeader>
                        <IconWrapper color="#facc15">
                          <SonarQubeIcon />
                        </IconWrapper>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>Code Quality</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>SonarQube Metrics</Typography>
                        </Box>
                      </SectionHeader>
                      <ModernTextField
                        fullWidth
                        label="Project Key"
                        name="sonarqube_project_key"
                        value={formData.sonarqube_project_key}
                        onChange={handleChange}
                      />
                    </SectionContainer>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SectionContainer sx={{ height: '100%' }}>
                      <SectionHeader>
                        <IconWrapper color="#0078d4">
                          <AzureIcon />
                        </IconWrapper>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>Cloud DevOps</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Azure Configuration</Typography>
                        </Box>
                      </SectionHeader>
                      <ModernTextField
                        fullWidth
                        label="ADO Handle"
                        name="ado_assignee_name"
                        value={formData.ado_assignee_name}
                        onChange={handleChange}
                      />
                    </SectionContainer>
                  </Grid>

                  {/* Bitbucket Section */}
                  <Grid item xs={12}>
                    <SectionContainer>
                      <SectionHeader>
                        <IconWrapper color="#2684ff">
                          <BitbucketIcon />
                        </IconWrapper>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>Legacy Repository</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Bitbucket Alignment</Typography>
                        </Box>
                      </SectionHeader>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <ModernTextField
                            fullWidth
                            label="Repository Slug"
                            name="bitbucket_repo_slug"
                            value={formData.bitbucket_repo_slug}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <ModernTextField
                            fullWidth
                            label="Bitbucket Username"
                            name="bitbucket_username"
                            value={formData.bitbucket_username}
                            onChange={handleChange}
                          />
                        </Grid>
                      </Grid>
                    </SectionContainer>
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                      <StyledButton
                        type="submit"
                        variant="contained"
                        size="large"
                      >
                        Commit Talent Settings
                      </StyledButton>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        </PremiumPaper>
      </CenteredContainer>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Box>
  );
}

export default EditTalentSettingsForm;